import React from "react";
import { Link, useLocation } from "react-router-dom";

const exploreLinks = [
  { to: "/", text: "Home" },
  { to: "/what-is-tarot-cards-complete-guide", text: "What Is Tarot?" },
  { to: "/yes-no-tarot", text: "Yes/No Tarot" },
  { to: "/readmycard", text: "Read My Card" },
  { to: "/tarotscope", text: "Tarotscope" },
  { to: "/fate-friday", text: "Fate Friday" },
  { to: "/full-moon", text: "Full Moon" },
];

const cardLinks = [
  { to: "/tarot-cards", text: "All Cards" },
  { to: "/tarot/major-arcana", text: "Major Arcana" },
  { to: "/tarot/wands", text: "Wands" },
  { to: "/tarot/cups", text: "Cups" },
  { to: "/tarot/swords", text: "Swords" },
  { to: "/tarot/pentacles", text: "Pentacles" },
];

const emotionLinks = [
  { to: "/emotions/uncertain-love", text: "Uncertain Love" },
  { to: "/emotions/heart-broken", text: "Heart Broken" },
  { to: "/emotions/anxious", text: "Anxious" },
  { to: "/emotions/confused", text: "Confused" },
  { to: "/emotions/lonely", text: "Lonely" },
  { to: "/emotions/hopeless", text: "Hopeless" },
  { to: "/emotions/guilty", text: "Guilty" },
  { to: "/emotions/betrayed", text: "Betrayed" },
  { to: "/emotions/insecure", text: "Insecure" },
  { to: "/emotions/overwhelmed", text: "Overwhelmed" },
  { to: "/emotions/other", text: "Other" },
];

const policyLinks = [
  { to: "/about", text: "About TarotFate" },
  { to: "/privacy", text: "Privacy Policy" },
  { to: "/disclaimer", text: "Disclaimer" },
];

function FooterLink({ to, children }) {
  return (
    <Link
      to={to}
      className="group -ml-1 inline-flex items-center gap-2 rounded-md px-1 py-0.5 text-white/88 transition duration-300 hover:translate-x-1 hover:bg-white/5 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75"
    >
      <span className="h-0 w-0 shrink-0 border-y-[4px] border-l-[6px] border-y-transparent border-l-[#d8c8ec]/55 transition duration-300 group-hover:translate-x-0.5 group-hover:border-l-[#f0e6ff]/85" />
      <span className="relative after:absolute after:-bottom-0.5 after:left-0 after:h-px after:w-0 after:bg-[linear-gradient(90deg,rgba(216,200,236,0.75),transparent)] after:transition-all after:duration-300 group-hover:after:w-full">
        {children}
      </span>
    </Link>
  );
}

function FooterHeading({ children }) {
  return (
    <h2 className="flex items-center gap-3 text-[13px] font-bold uppercase tracking-[0.22em] text-[#f2d38a]">
      <span className="h-px w-7 bg-[#f2d38a]/60" />
      <span>{children}</span>
    </h2>
  );
}

export default function Footer() {
  const location = useLocation();
  const isSpanishPath = location.pathname === "/es" || location.pathname.startsWith("/es/");
  const toLocalizedPath = (to) => {
    if (!isSpanishPath) return to;
    if (to === "/") return "/es";
    return `/es${to}`;
  };
  const copy = isSpanishPath
    ? {
        intro: "Una biblioteca gratuita de tarot y un espacio de lectura para reflexionar, comprender tus emociones y estudiar las cartas.",
        explore: "Explorar",
        cardLibrary: "Biblioteca de cartas",
        readByEmotion: "Leer por emocion",
        trust: "Confianza y politicas",
        rights: "Todos los derechos reservados.",
        exploreLinks: [
          "Inicio",
          "Que es el tarot?",
          "Tarot si o no",
          "Leer mis cartas",
          "Tarotscopo",
          "Viernes del destino",
          "Luna llena",
        ],
        cardLinks: ["Todas las cartas", "Arcanos mayores", "Bastos", "Copas", "Espadas", "Oros"],
        emotionLinks: [
          "Amor incierto",
          "Corazon roto",
          "Ansiedad",
          "Confusion",
          "Soledad",
          "Desesperanza",
          "Culpa",
          "Traicion",
          "Inseguridad",
          "Agobio",
          "Otra emocion",
        ],
        policyLinks: ["Sobre TarotFate", "Politica de privacidad", "Aviso legal"],
      }
    : {
        intro: "A free tarot library and reading space built for reflection, emotional clarity and card study.",
        explore: "Explore",
        cardLibrary: "Card Library",
        readByEmotion: "Read by Emotion",
        trust: "Trust & Policies",
        rights: "All rights reserved.",
        exploreLinks: exploreLinks.map((link) => link.text),
        cardLinks: cardLinks.map((link) => link.text),
        emotionLinks: emotionLinks.map((link) => link.text),
        policyLinks: policyLinks.map((link) => link.text),
      };

  return (
    <footer className="relative overflow-hidden border-t border-white/25 bg-[linear-gradient(180deg,#073b5d_0%,#084d6b_42%,#241934_100%)] text-white before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-[linear-gradient(90deg,transparent,rgba(216,200,236,0.65),rgba(156,231,238,0.5),transparent)]">
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,0.05)_0%,transparent_23%,transparent_76%,rgba(255,255,255,0.04)_100%)]" />

      <div className="relative mx-auto max-w-7xl px-5 py-12 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 gap-x-5 gap-y-9 md:gap-x-8 lg:grid-cols-[1.22fr_0.58fr_0.58fr_1.05fr_0.72fr] lg:gap-10 xl:gap-12">
          <section className="col-span-2 text-left lg:col-span-1">
            <Link
              to={toLocalizedPath("/")}
              className="inline-flex rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-white/65"
              aria-label="TarotFate home"
            >
              <img
                src="/icons/logo.webp"
                alt="TarotFate"
                className="h-auto w-[176px] max-w-full object-contain opacity-100 sm:w-[214px]"
                loading="lazy"
              />
            </Link>
            <p className="mt-4 max-w-sm text-[15px] leading-relaxed text-white/75">
              {copy.intro}
            </p>
            <div className="mt-6 h-px max-w-[260px] bg-[linear-gradient(90deg,rgba(216,200,236,0.55),rgba(156,231,238,0.28),transparent)]" />
          </section>

          <nav aria-label="Explore" className="text-left lg:border-l lg:border-white/10 lg:pl-6">
            <FooterHeading>{copy.explore}</FooterHeading>
            <ul className="mt-4 space-y-2.5 text-[16px]">
              {exploreLinks.map((link, index) => (
                <li key={link.to}>
                  <FooterLink to={toLocalizedPath(link.to)}>{copy.exploreLinks[index]}</FooterLink>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label="Card library" className="text-left lg:border-l lg:border-white/10 lg:pl-6">
            <FooterHeading>{copy.cardLibrary}</FooterHeading>
            <ul className="mt-4 space-y-2.5 text-[16px]">
              {cardLinks.map((link, index) => (
                <li key={link.to}>
                  <FooterLink to={toLocalizedPath(link.to)}>{copy.cardLinks[index]}</FooterLink>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label="Read by emotion" className="text-left lg:border-l lg:border-white/10 lg:pl-7">
            <FooterHeading>{copy.readByEmotion}</FooterHeading>
            <ul className="mt-4 grid grid-cols-1 gap-y-2.5 text-[16px] sm:grid-cols-2 sm:gap-x-5">
              {emotionLinks.map((link, index) => (
                <li key={link.to}>
                  <FooterLink to={toLocalizedPath(link.to)}>{copy.emotionLinks[index]}</FooterLink>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label="Trust and policies" className="text-left lg:justify-self-end lg:border-l lg:border-white/10 lg:pl-7">
            <FooterHeading>{copy.trust}</FooterHeading>
            <ul className="mt-4 space-y-2.5 text-[16px]">
              {policyLinks.map((link, index) => (
                <li key={link.to}>
                  <FooterLink to={toLocalizedPath(link.to)}>{copy.policyLinks[index]}</FooterLink>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="mt-10 border-t border-white/20 pt-5 text-left text-[13px] leading-relaxed text-white/64">
          <p>
            &copy; {new Date().getFullYear()} TarotFate.com. {copy.rights}
          </p>
        </div>
      </div>
    </footer>
  );
}

